import { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, Star } from 'lucide-react';
import { publicApi } from '../lib/api';

export interface VenuePlace {
  place_id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  rating?: number | null;
  ratings_total?: number | null;
}

/**
 * שדה חיפוש אולם עם השלמה אוטומטית מ-Google Places (Google My Business):
 * מקלידים "אודאון" ומקבלים את בית העסק האמיתי — שם רשמי, כתובת וקואורדינטות.
 */
export function VenueSearchInput({
  id,
  value,
  onChange,
  onSelect,
  placeholder = 'הקלידו שם אולם / גן אירועים…',
}: {
  id?: string;
  value: string;
  onChange: (text: string) => void;
  onSelect: (place: VenuePlace) => void;
  placeholder?: string;
}) {
  const [results, setResults] = useState<VenuePlace[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapRef = useRef<HTMLDivElement>(null);
  const skipNextSearch = useRef(false);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    const q = value.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await publicApi.get('/api/public/places/search', { params: { q } });
        const list: VenuePlace[] = Array.isArray(data?.results) ? data.results : [];
        setResults(list);
        setOpen(list.length > 0);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  function choose(place: VenuePlace) {
    skipNextSearch.current = true;
    onSelect(place);
    setOpen(false);
    setResults([]);
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          id={id}
          className="field pl-9"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none">
          {searching ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />}
        </span>
      </div>

      {open && (
        <div className="absolute z-30 mt-1.5 w-full rounded-xl border border-line bg-paper-elev shadow-lg overflow-hidden">
          {results.map((r) => (
            <button
              key={r.place_id}
              type="button"
              onClick={() => choose(r)}
              className="w-full text-right px-3.5 py-2.5 hover:bg-sage-50 transition-colors flex items-start gap-2.5 border-b border-line last:border-b-0 cursor-pointer"
            >
              <MapPin size={15} className="mt-0.5 shrink-0 text-sage-600" />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-ink truncate">{r.name}</span>
                {r.address && (
                  <span className="block text-[12px] text-ink-mute truncate">{r.address}</span>
                )}
              </span>
              {r.rating != null && (
                <span className="shrink-0 inline-flex items-center gap-1 text-[12px] font-bold text-ink-soft mt-0.5">
                  {r.rating.toFixed(1)}
                  <Star size={12} className="fill-[#E8A93C] text-[#E8A93C]" aria-hidden />
                  {r.ratings_total != null && (
                    <span className="font-normal text-ink-faint">({r.ratings_total.toLocaleString('he-IL')})</span>
                  )}
                </span>
              )}
            </button>
          ))}
          <div className="px-3.5 py-1.5 bg-paper-mist text-[10.5px] text-ink-faint text-right">
            תוצאות מ-Google — בחירה ממלאת כתובת ומיקום מדויק למפת הטרמפים
          </div>
        </div>
      )}
    </div>
  );
}
